// Note Expander v2, the review screens' pure logic (PR 5-UI-2, O191 and its addendum), kept out
// of the components so tests/unit/v3/note-expander-v2-review-lib.test.js drives it directly.
//
//   The server stays the authority: it computes the final note at accept (finalText.js), refuses
//   Maximal with a highlight untouched, verifies every ticked item and writes through the outbox.
//   What lives here is the screen: the note as sections → blocks → spans, the highlight counter,
//   a preview of the length, the accept body, and the outbox rows in words.

const SECTION_ORDER = Object.freeze(['hpi', 'vitals', 'exam', 'ap']);
const SECTION_LABELS = Object.freeze({ hpi: 'HPI', vitals: 'Vitals', exam: 'Physical Examination', ap: 'Assessment and Plan' });

const REMOVE_REASONS = Object.freeze([
  ['not_true', 'Not true'], ['not_my_style', 'Not my style'], ['too_long', 'Too long'], ['other', 'Other'],
]);

// The floor's cut groups (validate.js; never-invent.json) in words. A cut carries its group and
// a short term, never the text it removed.
const CUT_WORDS = Object.freeze({
  numbers: 'A number not in your stub or the chart',
  quotes: 'A quote not in your stub',
  unmentioned_exam: "An exam system your stub didn't mention",
  abnormal_findings: "An abnormal finding your stub doesn't state",
  diagnoses_and_codes: 'A diagnosis or code the stub and chart do not support',
  identity: 'Patient identifiers',
  format: 'Text the note could not use',
  items: 'A chart item with nothing in your stub behind it',
  remote_visit: 'Exam wording on a video or phone visit',
});

// The run's source decides what the web may do with it (O191 addendum): a manual run is written
// from here; an extension run (and PR 7's sweep runs) is finished in the chart.
function canWrite(run) { return !!run && run.source === 'manual'; }

// The revision's spans as the screen draws them, in the server's order.
function buildTree(spans) {
  const bySection = new Map();
  for (const s of spans || []) {
    if (!bySection.has(s.section)) bySection.set(s.section, new Map());
    const blocks = bySection.get(s.section);
    if (!blocks.has(s.blockSeq)) blocks.set(s.blockSeq, { blockSeq: s.blockSeq, heading: null, spans: [] });
    const block = blocks.get(s.blockSeq);
    if (!block.heading && s.heading) block.heading = s.heading; // every span of a block carries it
    block.spans.push(s);
  }
  const ordered = [...bySection.keys()].sort((a, b) => rank(a) - rank(b));
  return ordered.map((section) => ({
    section,
    label: SECTION_LABELS[section] || section,
    blocks: [...bySection.get(section).values()]
      .sort((a, b) => a.blockSeq - b.blockSeq)
      .map((b) => ({ ...b, spans: b.spans.slice().sort((x, y) => x.seq - y.seq) })),
  }));
}
function rank(section) { const i = SECTION_ORDER.indexOf(section); return i < 0 ? SECTION_ORDER.length : i; }

const isHighlight = (s) => s.tag === 'inferred';
function highlightCounts(spans) {
  const all = (spans || []).filter(isHighlight);
  return { total: all.length, open: all.filter((s) => !s.touch).length };
}
// "Keep all in this section": its highlights nobody has touched yet.
function untouchedInSection(spans, section) {
  return (spans || []).filter((s) => s.section === section && isHighlight(s) && !s.touch).map((s) => s.id);
}

// The note's length after the touches and the added suggestions, for the counter only: the
// server's finalNote is what is written (and refuses too_long).
function previewLength(renderedText, spans, suggestions, added) {
  let n = String(renderedText || '').length;
  for (const s of spans || []) {
    const len = String(s.text || '').length;
    if (s.touch === 'removed') n -= len + 1;
    else if (s.touch === 'edited' && typeof s.editedText === 'string') n += s.editedText.length - len;
  }
  for (const i of added || []) {
    const sg = (suggestions || [])[i];
    if (sg && typeof sg.text === 'string') n += sg.text.length + 1;
  }
  return Math.max(0, n);
}

// The span as it reads now.
function spanText(s) {
  if (s.touch === 'removed') return '';
  if (s.touch === 'edited' && typeof s.editedText === 'string') return s.editedText;
  return s.text || '';
}

function sourceWords(source) {
  if (!source) return '';
  if (source.kind === 'draft') return `From your stub: “${source.quote}”`;
  if (source.kind === 'chart') return `From the chart: ${source.label}`;
  if (source.kind === 'template') return 'Standard exam wording, cued by your stub';
  return '';
}

// The accept body (POST /v2/revisions/:id/accept) from the screen's choices.
//   added: Set of suggestion indexes · problems: Map index → { snomed? } · administrations:
//   Map index → { acknowledgeAllergy }
function acceptBody({ added, problems, administrations }) {
  return {
    addSuggestions: [...(added || [])].sort((a, b) => a - b),
    chartItems: {
      problems: [...(problems || new Map()).entries()].sort((a, b) => a[0] - b[0])
        .map(([index, p]) => ({ index, ...(p && p.snomed ? { snomed: String(p.snomed) } : {}) })),
      administrations: [...(administrations || new Map()).entries()].sort((a, b) => a[0] - b[0])
        .map(([index, a]) => ({ index, acknowledgeAllergy: !!(a && a.acknowledgeAllergy) })),
    },
  };
}

// A problem may be ticked when accept can write it: a curated code, or an option picked.
function problemTickable(item) {
  const st = item && item.dx && item.dx.status;
  return st === 'codeable' || st === 'options';
}
// An administration with an allergy conflict is written only with the clinician's explicit
// acknowledgement (the server's acknowledgeAllergy; pr4c2-report).
function administrationReady(item, choice) {
  if (!choice) return false;
  return !item.allergyConflict || !!choice.acknowledgeAllergy;
}

const OUTBOX_LABELS = Object.freeze({
  note: 'Expanded copy note, on the stub’s date, under the patient’s PCP',
  vitals: 'Vitals carried onto the copy',
  problem: 'Problem',
  immunization: 'Immunization',
  medication: 'Medication',
});
const OUTBOX_STATES = Object.freeze({
  pending: { words: 'Waiting to write', tone: 'running' },
  sending: { words: 'Writing…', tone: 'running' },
  confirmed: { words: 'Written', tone: 'done' },
  ambiguous: { words: 'Checking the chart before trying again', tone: 'running' },
});

// One outbox row → { label, words, tone }; a failed row says why in the server's words.
function outboxLine(row, copy) {
  const label = OUTBOX_LABELS[row.kind] || row.kind;
  if (row.status === 'failed') {
    const why = copy && copy.write && row.lastErrorCode && copy.write[row.lastErrorCode];
    return { label, words: why || 'Not written', tone: 'failed' };
  }
  const st = OUTBOX_STATES[row.status] || { words: row.status, tone: 'running' };
  return { label, words: st.words, tone: st.tone };
}
function outboxSettled(rows) {
  return (rows || []).length > 0 && rows.every((r) => r.status === 'confirmed' || r.status === 'failed');
}
// What "Undo chart items" may ask the server to undo: this revision's written chart items.
const UNDOABLE_KINDS = Object.freeze(['problem', 'immunization', 'medication']);
function undoable(rows) {
  return (rows || []).filter((r) => UNDOABLE_KINDS.includes(r.kind) && r.status === 'confirmed' && r.elationId)
    .map((r) => ({ kind: r.kind, elationId: String(r.elationId) }));
}

module.exports = {
  SECTION_LABELS, REMOVE_REASONS, CUT_WORDS, OUTBOX_LABELS, UNDOABLE_KINDS,
  canWrite, buildTree, highlightCounts, untouchedInSection, previewLength, spanText, sourceWords,
  acceptBody, problemTickable, administrationReady, outboxLine, outboxSettled, undoable,
};
